import { SelectGroups, SelectItem } from '@freelancer/ui/select';
import {
  DeloitteIndustryApi,
  DeloitteOfferingApi,
  DeloitteOfferingPortfolioApi,
  DeloittePracticeApi,
  DeloitteProjectTypeApi,
} from 'api-typings/projects/deloitte';

/**
 * Deloitte project details found in a Deloitte project
 */
export interface DeloitteProjectDetails {
  readonly billingCode: string;
  readonly industryOffering: DeloitteIndustryOffering;
  readonly clearance: string | null;
  readonly itar: boolean | null;
}

export interface DeloitteProjectDetailsGet {
  readonly billingCode: string;
  readonly industryOffering: DeloitteIndustryOffering;
  readonly clearance?: string;
  readonly itar?: string;
}

export interface DeloitteIndustryOffering {
  readonly projectType: DeloitteProjectTypeApi;
  readonly practice: DeloittePracticeApi;
  readonly industry: DeloitteIndustryApi;
  readonly offering: DeloitteOfferingApi;
}

// @see {@link https://phabricator.tools.flnltd.com/D125102|Deloitte billing code regex}
export const deloitteBillingCodeMatcher: RegExp = /([A-Z]{3}[\d]{5})-?([A-Z\d]{2})-?([A-Z\d]{2})-?([A-Z\d]{2})-?([\d]{4})/;
export const deloitteBillingCodePattern: RegExp = /^[A-Z]{3}[\d]{5}-?[A-Z\d]{2}-?[A-Z\d]{2}-?[A-Z\d]{2}-?[\d]{4}$/i;

export const DELOITTE_PROJECT_TYPE_DISPLAY_NAME_MAP: {
  readonly [k in DeloitteProjectTypeApi]: string;
} = {
  [DeloitteProjectTypeApi.CLIENT_BILLABLE]: 'Client Billable',
  [DeloitteProjectTypeApi.FIRM_INITIATIVE]: 'Firm Initiative',
  [DeloitteProjectTypeApi.PRD]: 'PRD',
  [DeloitteProjectTypeApi.OTHER_INVESTMENT]: 'Other Investments',
  [DeloitteProjectTypeApi.SKILLS_BASED_VOLUNTEERING]:
    'Skills-Based Volunteering',
};

export const DELOITTE_PRACTICE_DISPLAY_NAME_MAP: {
  readonly [k in DeloittePracticeApi]: string;
} = {
  [DeloittePracticeApi.COMMERCIAL_CORE]: 'Commercial Core',
  [DeloittePracticeApi.COMMERCIAL_USDC_ONSITE]: 'Commercial PDM',
  [DeloittePracticeApi.COMMERCIAL_USDC_INCENTER]: 'Commercial USDC',
  [DeloittePracticeApi.GPS_CORE]: 'GPS Core',
  [DeloittePracticeApi.GPS_USDC_ONSITE]: 'GPS PDM',
  [DeloittePracticeApi.GPS_USDC_INCENTER]: 'GPS USDC',
  [DeloittePracticeApi.USI_ONSITE]: 'USI Onsite',
  [DeloittePracticeApi.USI_OFFSHORE]: 'USI Offshore',
};

export const DELOITTE_INDUSTRY_DISPLAY_NAME_MAP: {
  readonly [k in DeloitteIndustryApi]: string;
} = {
  [DeloitteIndustryApi.AUTOMOTIVE]: 'Automative',
  [DeloitteIndustryApi.BANKING_AND_CAPITAL_MARKETS]:
    'Banking & Capital Markets',
  [DeloitteIndustryApi.CIVIL_GOVERNMENT]: 'Civil Government',
  [DeloitteIndustryApi.CONSUMER_PRODUCTS]: 'Consumer Products',
  [DeloitteIndustryApi.CROSS_SECTOR_C]: 'Cross Sector (C)',
  [DeloitteIndustryApi.CROSS_SECTOR_CI]: 'Cross Sector (CI)',
  [DeloitteIndustryApi.CROSS_SECTOR_ERI]: 'Cross Sector (ERI)',
  [DeloitteIndustryApi.CROSS_SECTOR_FS]: 'Cross Sector (FS)',
  [DeloitteIndustryApi.CROSS_SECTOR_GPS]: 'Cross Sector (GPS)',
  [DeloitteIndustryApi.CROSS_SECTOR_LSHC]: 'Cross Sector (LSHC)',
  [DeloitteIndustryApi.CROSS_SECTOR_TMT]: 'Cross Sector (TMT)',
  [DeloitteIndustryApi.DEFENSE_SECURITY_AND_JUSTICE]:
    'Defense Security & Justice',
  [DeloitteIndustryApi.FEDERAL_HEALTH]: 'Federal Health',
  [DeloitteIndustryApi.HEALTH_CARE]: 'Health Care',
  [DeloitteIndustryApi.INDUSTRIAL_PRODUCTS_AND_CONSTRUCTION]:
    'Industrial Products & Construction',
  [DeloitteIndustryApi.INSURANCE]: 'Insurance',
  [DeloitteIndustryApi.INVESTMENT_MANAGEMENT]: 'Investment Management',
  [DeloitteIndustryApi.MEDIA_AND_ENTERTAINMENT]: 'Media & Entertainment',
  [DeloitteIndustryApi.OIL_GAS_AND_CHEMICALS]: 'Oil, Gas, & Chemicals',
  [DeloitteIndustryApi.POWER_AND_UTILITIES]: 'Power & Utilities',
  [DeloitteIndustryApi.REAL_ESTATE]: 'Real Estate',
  [DeloitteIndustryApi.RETAIL_WHOLESALE_AND_DISTRIBUTION]:
    'Retail, Wholesale, & Distribution',
  [DeloitteIndustryApi.STATE_LOCAL_AND_HIGHER_EDUCATION]:
    'State, Local, & Higher Education',
  [DeloitteIndustryApi.TECHNOLOGY]: 'Technology',
  [DeloitteIndustryApi.TRANSPORTATION_HOSPITALITY_AND_SERVICES]:
    'Transportation, Hospitality, & Services',
  [DeloitteIndustryApi.TELECOM_MEDIA_AND_ENTERTAINMENT]:
    'Telecom, Media, & Entertainment',
};

export const DELOITTE_OFFERING_DISPLAY_NAME_MAP: {
  readonly [k in DeloitteOfferingApi]: string;
} = {
  [DeloitteOfferingApi.CBO_CROSS_CONSULTING_GROUP]: 'Cross Consulting Group',
  [DeloitteOfferingApi.CBO_OPERATIONS_TRANSFORMATION]:
    'Operations Transformation',
  [DeloitteOfferingApi.CBO_SYSTEMS_ENGINEERING]: 'Systems Engineering',
  [DeloitteOfferingApi.CBO_CLOUD_ENGINEERING]: 'Cloud Engineering',
  [DeloitteOfferingApi.CBO_CORE_INDUSTRY_SOLUTIONS]: 'Core Industry Solutions',
  [DeloitteOfferingApi.CBO_HEALTH_TECHNOLOGY]: 'Health Technology',
  [DeloitteOfferingApi.CNM_CUSTOMER_STRATEGY_AND_APPLIED_DESIGN]:
    'Customer Strategy & Applied Design',
  [DeloitteOfferingApi.CNM_ADVERTISING_MARKETING_AND_COMMERCE]:
    'Advertising, Marketing, & Commerce',
  [DeloitteOfferingApi.CNM_DIGITAL_CUSTOMER]: 'Digital Customer',
  [DeloitteOfferingApi.CNM_HUMAN_SERVICES_TRANSFORMATION]:
    'Human Services Transformation',
  [DeloitteOfferingApi.CNM_DIGITAL_GOVERNMENT]: 'Digital Government',
  [DeloitteOfferingApi.CNM_CROSS_CONSULTING_GROUP]: 'Cross Consulting Group',
  [DeloitteOfferingApi.EO_CROSS_CONSULTING_GROUP]: 'Cross Consulting Group',
  [DeloitteOfferingApi.EO_FINANCE_AND_ENTERPRISE_PERFORMANCE]:
    'Finance & Enterprise Performance',
  [DeloitteOfferingApi.EO_SUPPLY_CHAIN_AND_NETWORK_OPERATIONS]:
    'Supply Chain & Network Operations',
  [DeloitteOfferingApi.EO_TECHNOLOGY_SERVICES_OPTIMIZATION]:
    'Technology Services Optimization',
  [DeloitteOfferingApi.EO_SAP]: 'SAP',
  [DeloitteOfferingApi.EO_ORACLE]: 'Oracle',
  [DeloitteOfferingApi.EO_ERP_SAAS]: 'ERP SaaS',
  [DeloitteOfferingApi.HC_CROSS_CONSULTING_GROUP]: 'Cross Consulting Group',
  [DeloitteOfferingApi.HC_HR_TRANSFORMATION]: 'HR Transformation',
  [DeloitteOfferingApi.HC_ORGANIZATION_TRANSFORMATION]:
    'Organization Transformation',
  [DeloitteOfferingApi.HC_WORKFORCE_TRANSFORMATION]: 'Workforce Transformation',
  [DeloitteOfferingApi.HC_HC_AS_A_SERVICE]: 'HC as a Service',
  [DeloitteOfferingApi.MNA_MERGERS_AND_ACQUISITIONS]: 'Mergers & Acquisitions',
  [DeloitteOfferingApi.SNA_CROSS_CONSULTING_GROUP]: 'Cross Consulting Group',
  [DeloitteOfferingApi.SNA_STRATEGY]: 'Strategy',
  [DeloitteOfferingApi.SNA_ANALYTICS_AND_COGNITIVE]: 'Analytics & Cognitive',
  [DeloitteOfferingApi.SNA_HYBRID_SOLUTIONS_AND_INCUBATION]:
    'Hybrid Solutions & Incubation',
};

export const DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP: {
  readonly [k in DeloitteOfferingPortfolioApi]: string;
} = {
  [DeloitteOfferingPortfolioApi.CORE_BUSINESS_OPERATIONS]:
    'Core Business Operations',
  [DeloitteOfferingPortfolioApi.CUSTOMER_AND_MARKETING]: 'Customer & Marketing',
  [DeloitteOfferingPortfolioApi.ENTERPRISE_OPERATIONS]: 'Enterprise Operations',
  [DeloitteOfferingPortfolioApi.HUMAN_CAPITAL]: 'Human Capital',
  [DeloitteOfferingPortfolioApi.MERGERS_AND_ACQUISITIONS]:
    'Mergers & Acquisitions',
  [DeloitteOfferingPortfolioApi.STRATEGY_AND_ANALYTICS]: 'Strategy & Analytics',
};

export const DELOITTE_OFFERING_FULL_DISPLAY_NAME_MAP: {
  readonly [k in DeloitteOfferingApi]: string;
} = {
  [DeloitteOfferingApi.CBO_CROSS_CONSULTING_GROUP]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.CORE_BUSINESS_OPERATIONS
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.CBO_CROSS_CONSULTING_GROUP
    ]
  }`,
  [DeloitteOfferingApi.CBO_OPERATIONS_TRANSFORMATION]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.CORE_BUSINESS_OPERATIONS
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.CBO_OPERATIONS_TRANSFORMATION
    ]
  }`,
  [DeloitteOfferingApi.CBO_SYSTEMS_ENGINEERING]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.CORE_BUSINESS_OPERATIONS
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.CBO_SYSTEMS_ENGINEERING
    ]
  }`,
  [DeloitteOfferingApi.CBO_CLOUD_ENGINEERING]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.CORE_BUSINESS_OPERATIONS
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.CBO_CLOUD_ENGINEERING
    ]
  }`,
  [DeloitteOfferingApi.CBO_CORE_INDUSTRY_SOLUTIONS]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.CORE_BUSINESS_OPERATIONS
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.CBO_CORE_INDUSTRY_SOLUTIONS
    ]
  }`,
  [DeloitteOfferingApi.CBO_HEALTH_TECHNOLOGY]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.CORE_BUSINESS_OPERATIONS
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.CBO_HEALTH_TECHNOLOGY
    ]
  }`,
  [DeloitteOfferingApi.CNM_CUSTOMER_STRATEGY_AND_APPLIED_DESIGN]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.CUSTOMER_AND_MARKETING
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.CNM_CUSTOMER_STRATEGY_AND_APPLIED_DESIGN
    ]
  }`,
  [DeloitteOfferingApi.CNM_ADVERTISING_MARKETING_AND_COMMERCE]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.CUSTOMER_AND_MARKETING
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.CNM_ADVERTISING_MARKETING_AND_COMMERCE
    ]
  }`,
  [DeloitteOfferingApi.CNM_DIGITAL_CUSTOMER]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.CUSTOMER_AND_MARKETING
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[DeloitteOfferingApi.CNM_DIGITAL_CUSTOMER]
  }`,
  [DeloitteOfferingApi.CNM_HUMAN_SERVICES_TRANSFORMATION]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.CUSTOMER_AND_MARKETING
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.CNM_HUMAN_SERVICES_TRANSFORMATION
    ]
  }`,
  [DeloitteOfferingApi.CNM_DIGITAL_GOVERNMENT]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.CUSTOMER_AND_MARKETING
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.CNM_DIGITAL_GOVERNMENT
    ]
  }`,
  [DeloitteOfferingApi.CNM_CROSS_CONSULTING_GROUP]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.CUSTOMER_AND_MARKETING
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.CNM_CROSS_CONSULTING_GROUP
    ]
  }`,
  [DeloitteOfferingApi.EO_CROSS_CONSULTING_GROUP]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.ENTERPRISE_OPERATIONS
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.EO_CROSS_CONSULTING_GROUP
    ]
  }`,
  [DeloitteOfferingApi.EO_FINANCE_AND_ENTERPRISE_PERFORMANCE]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.ENTERPRISE_OPERATIONS
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.EO_FINANCE_AND_ENTERPRISE_PERFORMANCE
    ]
  }`,
  [DeloitteOfferingApi.EO_SUPPLY_CHAIN_AND_NETWORK_OPERATIONS]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.ENTERPRISE_OPERATIONS
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.EO_SUPPLY_CHAIN_AND_NETWORK_OPERATIONS
    ]
  }`,
  [DeloitteOfferingApi.EO_TECHNOLOGY_SERVICES_OPTIMIZATION]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.ENTERPRISE_OPERATIONS
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.EO_TECHNOLOGY_SERVICES_OPTIMIZATION
    ]
  }`,
  [DeloitteOfferingApi.EO_SAP]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.ENTERPRISE_OPERATIONS
    ]
  }, ${DELOITTE_OFFERING_DISPLAY_NAME_MAP[DeloitteOfferingApi.EO_SAP]}`,
  [DeloitteOfferingApi.EO_ORACLE]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.ENTERPRISE_OPERATIONS
    ]
  }, ${DELOITTE_OFFERING_DISPLAY_NAME_MAP[DeloitteOfferingApi.EO_ORACLE]}`,
  [DeloitteOfferingApi.EO_ERP_SAAS]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.ENTERPRISE_OPERATIONS
    ]
  }, ${DELOITTE_OFFERING_DISPLAY_NAME_MAP[DeloitteOfferingApi.EO_ERP_SAAS]}`,
  [DeloitteOfferingApi.HC_CROSS_CONSULTING_GROUP]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.HUMAN_CAPITAL
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.HC_CROSS_CONSULTING_GROUP
    ]
  }`,
  [DeloitteOfferingApi.HC_HR_TRANSFORMATION]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.HUMAN_CAPITAL
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[DeloitteOfferingApi.HC_HR_TRANSFORMATION]
  }`,
  [DeloitteOfferingApi.HC_ORGANIZATION_TRANSFORMATION]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.HUMAN_CAPITAL
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.HC_ORGANIZATION_TRANSFORMATION
    ]
  }`,
  [DeloitteOfferingApi.HC_WORKFORCE_TRANSFORMATION]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.HUMAN_CAPITAL
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.HC_WORKFORCE_TRANSFORMATION
    ]
  }`,
  [DeloitteOfferingApi.HC_HC_AS_A_SERVICE]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.HUMAN_CAPITAL
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[DeloitteOfferingApi.HC_HC_AS_A_SERVICE]
  }`,
  [DeloitteOfferingApi.MNA_MERGERS_AND_ACQUISITIONS]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.MERGERS_AND_ACQUISITIONS
    ]
  }`,
  [DeloitteOfferingApi.SNA_CROSS_CONSULTING_GROUP]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.STRATEGY_AND_ANALYTICS
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.SNA_CROSS_CONSULTING_GROUP
    ]
  }`,
  [DeloitteOfferingApi.SNA_STRATEGY]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.STRATEGY_AND_ANALYTICS
    ]
  }, ${DELOITTE_OFFERING_DISPLAY_NAME_MAP[DeloitteOfferingApi.SNA_STRATEGY]}`,
  [DeloitteOfferingApi.SNA_ANALYTICS_AND_COGNITIVE]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.STRATEGY_AND_ANALYTICS
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.SNA_ANALYTICS_AND_COGNITIVE
    ]
  }`,
  [DeloitteOfferingApi.SNA_HYBRID_SOLUTIONS_AND_INCUBATION]: `${
    DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
      DeloitteOfferingPortfolioApi.STRATEGY_AND_ANALYTICS
    ]
  }, ${
    DELOITTE_OFFERING_DISPLAY_NAME_MAP[
      DeloitteOfferingApi.SNA_HYBRID_SOLUTIONS_AND_INCUBATION
    ]
  }`,
};
export const PROJECT_TYPE_OPTIONS: ReadonlyArray<SelectItem> = [
  {
    displayText: '',
    value: '',
    disabled: true,
  },
  {
    displayText:
      DELOITTE_PROJECT_TYPE_DISPLAY_NAME_MAP[
        DeloitteProjectTypeApi.CLIENT_BILLABLE
      ],
    value: DeloitteProjectTypeApi.CLIENT_BILLABLE,
  },
  {
    displayText:
      DELOITTE_PROJECT_TYPE_DISPLAY_NAME_MAP[
        DeloitteProjectTypeApi.FIRM_INITIATIVE
      ],
    value: DeloitteProjectTypeApi.FIRM_INITIATIVE,
  },
  {
    displayText:
      DELOITTE_PROJECT_TYPE_DISPLAY_NAME_MAP[DeloitteProjectTypeApi.PRD],
    value: DeloitteProjectTypeApi.PRD,
  },
  {
    displayText:
      DELOITTE_PROJECT_TYPE_DISPLAY_NAME_MAP[
        DeloitteProjectTypeApi.SKILLS_BASED_VOLUNTEERING
      ],
    value: DeloitteProjectTypeApi.SKILLS_BASED_VOLUNTEERING,
  },
  {
    displayText:
      DELOITTE_PROJECT_TYPE_DISPLAY_NAME_MAP[
        DeloitteProjectTypeApi.OTHER_INVESTMENT
      ],
    value: DeloitteProjectTypeApi.OTHER_INVESTMENT,
  },
];

export const PRACTICE_OPTIONS: ReadonlyArray<SelectItem> = [
  {
    displayText: '',
    value: '',
    disabled: true,
  },
  {
    displayText:
      DELOITTE_PRACTICE_DISPLAY_NAME_MAP[DeloittePracticeApi.COMMERCIAL_CORE],
    value: DeloittePracticeApi.COMMERCIAL_CORE,
  },
  {
    displayText:
      DELOITTE_PRACTICE_DISPLAY_NAME_MAP[
        DeloittePracticeApi.COMMERCIAL_USDC_ONSITE
      ],
    value: DeloittePracticeApi.COMMERCIAL_USDC_ONSITE,
  },
  {
    displayText:
      DELOITTE_PRACTICE_DISPLAY_NAME_MAP[
        DeloittePracticeApi.COMMERCIAL_USDC_INCENTER
      ],
    value: DeloittePracticeApi.COMMERCIAL_USDC_INCENTER,
  },
  {
    displayText:
      DELOITTE_PRACTICE_DISPLAY_NAME_MAP[DeloittePracticeApi.GPS_CORE],
    value: DeloittePracticeApi.GPS_CORE,
  },
  {
    displayText:
      DELOITTE_PRACTICE_DISPLAY_NAME_MAP[DeloittePracticeApi.GPS_USDC_ONSITE],
    value: DeloittePracticeApi.GPS_USDC_ONSITE,
  },
  {
    displayText:
      DELOITTE_PRACTICE_DISPLAY_NAME_MAP[DeloittePracticeApi.GPS_USDC_INCENTER],
    value: DeloittePracticeApi.GPS_USDC_INCENTER,
  },
  {
    displayText:
      DELOITTE_PRACTICE_DISPLAY_NAME_MAP[DeloittePracticeApi.USI_OFFSHORE],
    value: DeloittePracticeApi.USI_OFFSHORE,
  },
  {
    displayText:
      DELOITTE_PRACTICE_DISPLAY_NAME_MAP[DeloittePracticeApi.USI_ONSITE],
    value: DeloittePracticeApi.USI_ONSITE,
  },
];

export const INDUSTRY_OPTIONS: ReadonlyArray<SelectItem> = [
  {
    displayText: '',
    value: '',
    disabled: true,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[DeloitteIndustryApi.AUTOMOTIVE],
    value: DeloitteIndustryApi.AUTOMOTIVE,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[
        DeloitteIndustryApi.BANKING_AND_CAPITAL_MARKETS
      ],
    value: DeloitteIndustryApi.BANKING_AND_CAPITAL_MARKETS,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[DeloitteIndustryApi.CIVIL_GOVERNMENT],
    value: DeloitteIndustryApi.CIVIL_GOVERNMENT,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[DeloitteIndustryApi.CONSUMER_PRODUCTS],
    value: DeloitteIndustryApi.CONSUMER_PRODUCTS,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[DeloitteIndustryApi.CROSS_SECTOR_C],
    value: DeloitteIndustryApi.CROSS_SECTOR_C,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[DeloitteIndustryApi.CROSS_SECTOR_CI],
    value: DeloitteIndustryApi.CROSS_SECTOR_CI,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[DeloitteIndustryApi.CROSS_SECTOR_ERI],
    value: DeloitteIndustryApi.CROSS_SECTOR_ERI,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[DeloitteIndustryApi.CROSS_SECTOR_FS],
    value: DeloitteIndustryApi.CROSS_SECTOR_FS,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[DeloitteIndustryApi.CROSS_SECTOR_GPS],
    value: DeloitteIndustryApi.CROSS_SECTOR_GPS,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[DeloitteIndustryApi.CROSS_SECTOR_LSHC],
    value: DeloitteIndustryApi.CROSS_SECTOR_LSHC,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[DeloitteIndustryApi.CROSS_SECTOR_TMT],
    value: DeloitteIndustryApi.CROSS_SECTOR_TMT,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[
        DeloitteIndustryApi.DEFENSE_SECURITY_AND_JUSTICE
      ],
    value: DeloitteIndustryApi.DEFENSE_SECURITY_AND_JUSTICE,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[DeloitteIndustryApi.FEDERAL_HEALTH],
    value: DeloitteIndustryApi.FEDERAL_HEALTH,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[DeloitteIndustryApi.HEALTH_CARE],
    value: DeloitteIndustryApi.HEALTH_CARE,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[
        DeloitteIndustryApi.INDUSTRIAL_PRODUCTS_AND_CONSTRUCTION
      ],
    value: DeloitteIndustryApi.INDUSTRIAL_PRODUCTS_AND_CONSTRUCTION,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[DeloitteIndustryApi.INSURANCE],
    value: DeloitteIndustryApi.INSURANCE,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[
        DeloitteIndustryApi.INVESTMENT_MANAGEMENT
      ],
    value: DeloitteIndustryApi.INVESTMENT_MANAGEMENT,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[
        DeloitteIndustryApi.MEDIA_AND_ENTERTAINMENT
      ],
    value: DeloitteIndustryApi.MEDIA_AND_ENTERTAINMENT,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[
        DeloitteIndustryApi.OIL_GAS_AND_CHEMICALS
      ],
    value: DeloitteIndustryApi.OIL_GAS_AND_CHEMICALS,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[
        DeloitteIndustryApi.POWER_AND_UTILITIES
      ],
    value: DeloitteIndustryApi.POWER_AND_UTILITIES,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[DeloitteIndustryApi.REAL_ESTATE],
    value: DeloitteIndustryApi.REAL_ESTATE,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[
        DeloitteIndustryApi.RETAIL_WHOLESALE_AND_DISTRIBUTION
      ],
    value: DeloitteIndustryApi.RETAIL_WHOLESALE_AND_DISTRIBUTION,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[
        DeloitteIndustryApi.STATE_LOCAL_AND_HIGHER_EDUCATION
      ],
    value: DeloitteIndustryApi.STATE_LOCAL_AND_HIGHER_EDUCATION,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[DeloitteIndustryApi.TECHNOLOGY],
    value: DeloitteIndustryApi.TECHNOLOGY,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[
        DeloitteIndustryApi.TELECOM_MEDIA_AND_ENTERTAINMENT
      ],
    value: DeloitteIndustryApi.TELECOM_MEDIA_AND_ENTERTAINMENT,
  },
  {
    displayText:
      DELOITTE_INDUSTRY_DISPLAY_NAME_MAP[
        DeloitteIndustryApi.TRANSPORTATION_HOSPITALITY_AND_SERVICES
      ],
    value: DeloitteIndustryApi.TRANSPORTATION_HOSPITALITY_AND_SERVICES,
  },
];

export const OFFERING_OPTIONS: ReadonlyArray<SelectGroups | SelectItem> = [
  {
    displayText: '',
    value: '',
    disabled: true,
  },
  {
    groupName:
      DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
        DeloitteOfferingPortfolioApi.CORE_BUSINESS_OPERATIONS
      ],
    options: [
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.CBO_CLOUD_ENGINEERING
          ],
        value: DeloitteOfferingApi.CBO_CLOUD_ENGINEERING,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.CBO_CORE_INDUSTRY_SOLUTIONS
          ],
        value: DeloitteOfferingApi.CBO_CORE_INDUSTRY_SOLUTIONS,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.CBO_CROSS_CONSULTING_GROUP
          ],
        value: DeloitteOfferingApi.CBO_CROSS_CONSULTING_GROUP,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.CBO_HEALTH_TECHNOLOGY
          ],
        value: DeloitteOfferingApi.CBO_HEALTH_TECHNOLOGY,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.CBO_OPERATIONS_TRANSFORMATION
          ],
        value: DeloitteOfferingApi.CBO_OPERATIONS_TRANSFORMATION,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.CBO_SYSTEMS_ENGINEERING
          ],
        value: DeloitteOfferingApi.CBO_SYSTEMS_ENGINEERING,
      },
    ],
  },
  {
    groupName:
      DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
        DeloitteOfferingPortfolioApi.CUSTOMER_AND_MARKETING
      ],
    options: [
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.CNM_ADVERTISING_MARKETING_AND_COMMERCE
          ],
        value: DeloitteOfferingApi.CNM_ADVERTISING_MARKETING_AND_COMMERCE,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.CNM_CROSS_CONSULTING_GROUP
          ],
        value: DeloitteOfferingApi.CNM_CROSS_CONSULTING_GROUP,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.CNM_CUSTOMER_STRATEGY_AND_APPLIED_DESIGN
          ],
        value: DeloitteOfferingApi.CNM_CUSTOMER_STRATEGY_AND_APPLIED_DESIGN,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.CNM_DIGITAL_CUSTOMER
          ],
        value: DeloitteOfferingApi.CNM_DIGITAL_CUSTOMER,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.CNM_DIGITAL_GOVERNMENT
          ],
        value: DeloitteOfferingApi.CNM_DIGITAL_GOVERNMENT,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.CNM_HUMAN_SERVICES_TRANSFORMATION
          ],
        value: DeloitteOfferingApi.CNM_HUMAN_SERVICES_TRANSFORMATION,
      },
    ],
  },
  {
    groupName:
      DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
        DeloitteOfferingPortfolioApi.ENTERPRISE_OPERATIONS
      ],
    options: [
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.EO_CROSS_CONSULTING_GROUP
          ],
        value: DeloitteOfferingApi.EO_CROSS_CONSULTING_GROUP,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[DeloitteOfferingApi.EO_ERP_SAAS],
        value: DeloitteOfferingApi.EO_ERP_SAAS,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.EO_FINANCE_AND_ENTERPRISE_PERFORMANCE
          ],
        value: DeloitteOfferingApi.EO_FINANCE_AND_ENTERPRISE_PERFORMANCE,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[DeloitteOfferingApi.EO_ORACLE],
        value: DeloitteOfferingApi.EO_ORACLE,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[DeloitteOfferingApi.EO_SAP],
        value: DeloitteOfferingApi.EO_SAP,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.EO_SUPPLY_CHAIN_AND_NETWORK_OPERATIONS
          ],
        value: DeloitteOfferingApi.EO_SUPPLY_CHAIN_AND_NETWORK_OPERATIONS,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.EO_TECHNOLOGY_SERVICES_OPTIMIZATION
          ],
        value: DeloitteOfferingApi.EO_TECHNOLOGY_SERVICES_OPTIMIZATION,
      },
    ],
  },
  {
    groupName:
      DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
        DeloitteOfferingPortfolioApi.HUMAN_CAPITAL
      ],
    options: [
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.HC_CROSS_CONSULTING_GROUP
          ],
        value: DeloitteOfferingApi.HC_CROSS_CONSULTING_GROUP,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.HC_HC_AS_A_SERVICE
          ],
        value: DeloitteOfferingApi.HC_HC_AS_A_SERVICE,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.HC_HR_TRANSFORMATION
          ],
        value: DeloitteOfferingApi.HC_HR_TRANSFORMATION,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.HC_ORGANIZATION_TRANSFORMATION
          ],
        value: DeloitteOfferingApi.HC_ORGANIZATION_TRANSFORMATION,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.HC_WORKFORCE_TRANSFORMATION
          ],
        value: DeloitteOfferingApi.HC_WORKFORCE_TRANSFORMATION,
      },
    ],
  },
  {
    groupName:
      DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
        DeloitteOfferingPortfolioApi.MERGERS_AND_ACQUISITIONS
      ],
    options: [
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.MNA_MERGERS_AND_ACQUISITIONS
          ],
        value: DeloitteOfferingApi.MNA_MERGERS_AND_ACQUISITIONS,
      },
    ],
  },
  {
    groupName:
      DELOITTE_OFFERING_PORTFOLIO_DISPLAY_NAME_MAP[
        DeloitteOfferingPortfolioApi.STRATEGY_AND_ANALYTICS
      ],
    options: [
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.SNA_ANALYTICS_AND_COGNITIVE
          ],
        value: DeloitteOfferingApi.SNA_ANALYTICS_AND_COGNITIVE,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.SNA_CROSS_CONSULTING_GROUP
          ],
        value: DeloitteOfferingApi.SNA_CROSS_CONSULTING_GROUP,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[
            DeloitteOfferingApi.SNA_HYBRID_SOLUTIONS_AND_INCUBATION
          ],
        value: DeloitteOfferingApi.SNA_HYBRID_SOLUTIONS_AND_INCUBATION,
      },
      {
        displayText:
          DELOITTE_OFFERING_DISPLAY_NAME_MAP[DeloitteOfferingApi.SNA_STRATEGY],
        value: DeloitteOfferingApi.SNA_STRATEGY,
      },
    ],
  },
];
